import { useState } from "react";
import { useNossaHistoria, TimelineEvent } from "@/hooks/useNossaHistoria";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Trash2, Edit2, Plus, Save, X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export function TimelineManager() {
  const { timelineEvents, timelineLoading, updateTimelineEvent, createTimelineEvent, deleteTimelineEvent } = useNossaHistoria();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<Partial<TimelineEvent>>({
    year: '',
    title: '',
    description: '',
    is_active: true,
    display_order: 0
  });

  const handleEdit = (event: TimelineEvent) => {
    setEditingId(event.id);
    setFormData(event);
    setIsCreating(false);
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingId(null);
    setFormData({
      year: new Date().getFullYear().toString(),
      title: '',
      description: '',
      is_active: true,
      display_order: (timelineEvents?.length || 0)
    });
  };

  const handleSave = () => {
    if (!formData.year || !formData.title || !formData.description) {
      return;
    }

    if (isCreating) {
      createTimelineEvent(formData as Omit<TimelineEvent, 'id'>);
      setIsCreating(false);
    } else if (editingId) {
      updateTimelineEvent({ id: editingId, updates: formData });
      setEditingId(null);
    }
    
    setFormData({
      year: '',
      title: '',
      description: '',
      is_active: true,
      display_order: 0
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsCreating(false);
    setFormData({
      year: '',
      title: '',
      description: '',
      is_active: true,
      display_order: 0
    });
  };

  if (timelineLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Eventos da Timeline</h3>
        <Button onClick={handleCreate} disabled={isCreating || editingId !== null}>
          <Plus className="w-4 h-4 mr-2" />
          Adicionar Evento
        </Button>
      </div>

      {(isCreating || editingId) && (
        <Card className="border-2 border-primary">
          <CardHeader>
            <CardTitle>{isCreating ? 'Novo Evento' : 'Editar Evento'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Ano (4-10 caracteres)</Label>
              <Input
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                maxLength={10}
                placeholder="Ex: 2024"
              />
            </div>

            <div>
              <Label>Título (2-200 caracteres)</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                maxLength={200}
                placeholder="Ex: Formação em Nutrição"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.title?.length || 0}/200
              </p>
            </div>

            <div>
              <Label>Descrição (10-500 caracteres)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                maxLength={500}
                rows={4}
                placeholder="Descrição do evento..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.description?.length || 0}/500
              </p>
            </div>

            <div>
              <Label>Ordem de Exibição</Label>
              <Input
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                min={0}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label>Ativo</Label>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={!formData.year || !formData.title || !formData.description}>
                <Save className="w-4 h-4 mr-2" />
                Salvar
              </Button>
              <Button onClick={handleCancel} variant="outline">
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {timelineEvents?.map((event) => (
          <Card key={event.id}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold bg-primary/10 text-primary px-3 py-1 rounded-full">
                      {event.year}
                    </span>
                    <h4 className="font-semibold">{event.title}</h4>
                    {!event.is_active && (
                      <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                        Inativo
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{event.description}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Ordem: {event.display_order}
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(event)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      if (confirm('Tem certeza que deseja deletar este evento?')) {
                        deleteTimelineEvent(event.id);
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}